/* 2026-08-31: see tc-fitting-room.css — Cloudflare serves this path with the
   query string ignored, so browsers are held on an old copy and the promo
   ribbon below never reaches them. */
/*
 * Promotion panels, drawn from whatever the server says is running.
 *
 * There is deliberately nothing here about father and son, or about a free
 * shirt. A panel arrives as a heading, an optional line, an optional badge, an
 * optional note about conditions and an optional product to point at, and this
 * draws those. Which promotions exist, what they say and which products they
 * appear on are all decided in the promotions calendar.
 *
 * That is the whole point of it. A new offer used to mean a theme change here
 * and an app release on two stores — two days to a week — while promotions are
 * often set up the day before they start. Now the same JSON feeds the website
 * and both phones, and an offer appears everywhere on the minute it opens.
 *
 * Fetched rather than rendered in Liquid because a panel is time-bound and the
 * page is cached: Liquid would serve yesterday's offer to whoever got the
 * cached copy. The trade is that a crawler does not see it, which is right —
 * a promotion is not what the page is about.
 */
(function () {
  var mount = document.querySelector('[data-tc-panels]');
  if (!mount) return;

  var handle = mount.getAttribute('data-handle');
  if (!handle) return;

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /* The number, pulled out of the badge so it can be set in the display face.
     "10% OFF BOTH" reads as one loud thing with the ten enlarged, and as a
     shouty sentence without it. */
  /** The offer the corner ribbon is showing, so no panel repeats it. */
  var RIBBONED = null;

  function splitBadge(badge) {
    var m = String(badge || '').match(/^(\d+%|FREE)\s*(.*)$/i);
    return m ? { lead: m[1], rest: m[2] } : { lead: null, rest: badge };
  }

  /*
   * What the corner says, in two words.
   *
   * A percentage is followed by OFF — "10% OFF" — and that used to be done to
   * every badge with a lead, which turned "FREE SHIRT" into "FREE OFF": not
   * English, and it threw away the only word that made the label true. The API
   * names the gift on purpose, because a label reading FREE in the corner of a
   * £275 suit says the suit is free.
   */
  function ribbonWhat(badge) {
    var parts = splitBadge(badge);
    if (!parts.lead) return badge;
    if (/^\d+%$/.test(parts.lead)) return parts.lead + ' OFF';
    var thing = String(parts.rest || '').split(/\s+/)[0];
    return thing ? parts.lead + ' ' + thing : parts.lead;
  }

  /*
   * A name short enough for a corner.
   *
   * The heading is the promotion's title in the calendar — "Father & Son Set
   * - 10% OFF", "FREE SHIRT WITH EVERY SUIT" — written to be read in a list
   * and far too long for a label on a photograph. Everything after a dash is
   * the number said again; a trailing "Set" or "Offer" says nothing; and what
   * is left is cut to two words, because the corner has to name the offer,
   * not describe it. The number is on the line underneath.
   */
  function ribbonName(heading) {
    var name = String(heading || '').split(/\s[-\u2013\u2014]\s/)[0].trim();
    name = name.replace(/\s+(set|offer|deal|promotion|promo)$/i, '').trim();
    var words = name.split(/\s+/);
    return words.length > 2 && name.length > 16 ? words.slice(0, 2).join(' ') : name;
  }

  /*
   * A corner on the photograph.
   *
   * The offer has to be seen by somebody who has not scrolled yet, and the
   * gallery is the only thing on a product page that is always above the fold.
   * A ribbon there costs no room in the buying column, which is the column
   * everything else is competing for.
   *
   * Placed once, and removed if the panel is ever redrawn, so switching
   * between products in a linked-variant row cannot leave two of them.
   */
  function ribbon(panel) {
    /* The frame around the main photograph, named exactly. A loose
       [class*="gallery"] match found .tcfr__under-gallery first and pinned the
       ribbon a thousand pixels down the page, under the thumbnails. */
    var gallery = document.querySelector('.tcfr__gal-main');
    if (!gallery || gallery.querySelector('.tc-ribbon')) return;
    if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';

    var tag = el('div', 'tc-ribbon');
    /*
     * The name, not the whole title.
     *
     * The heading is the promotion's name in the calendar — "Father & Son Set
     * - 10% OFF" — which carries the offer twice once the number is set
     * underneath it, and made the corner label half the width of the
     * photograph. Everything from a dash onwards is the number restated, so it
     * goes.
     */
    /*
     * The name, then the offer — unless they are the same words.
     *
     * The corner carries two lines: the promotion's name in gold above what it
     * gives in white. For "Father & Son Set - 10% OFF" that reads Father & Son
     * over 10% OFF, which is the point of having two. For "FREE SHIRT WITH
     * EVERY SUIT" both lines came out FREE SHIRT — the name trimmed to two
     * words and the badge, arriving at the same place — so the ribbon printed
     * it twice, once gold and once white, and read as two badges stacked.
     *
     * Where they agree, one line — and it wears the gold.
     *
     * Two attempts sat in between. Keeping the white line lost the gold, which
     * is the half that makes the corner look like ours rather than like a
     * discount plugin. Handing the eyebrow the rest of the heading read "WITH
     * EVERY SUIT" over "FREE SHIRT", which says two things but does not fit:
     * the eyebrow is capped at 118px so the photograph stays visible, and it
     * came out ellipsised.
     *
     * So the corner says FREE SHIRT once, in gold, at the offer line's size.
     * It is only ever drawn on a suit that has earned the offer.
     */
    var who = ribbonName(panel.heading);
    var what = ribbonWhat(panel.badge);
    var same = who && what && who.toLowerCase() === what.toLowerCase();
    if (who && !same) tag.appendChild(el('span', 'tc-ribbon__who', who));
    if (same) tag.classList.add('tc-ribbon--solo');
    tag.appendChild(el('strong', 'tc-ribbon__what' + (same ? ' tc-ribbon__what--solo' : ''), what));
    gallery.appendChild(tag);

    /*
     * On the photograph, not on the frame around it.
     *
     * The image element fills its container but is drawn object-fit: contain,
     * so a portrait suit shot sits letterboxed inside a square box with fifty
     * pixels of page either side. A ribbon at the container's corner floats in
     * that margin, next to the photo rather than on it.
     *
     * The drawn size is worked out from the natural dimensions, which is the
     * only way to know where contain actually put the picture.
     */
    function place() {
      var img = gallery.querySelector('img');
      if (!img || !img.naturalWidth || !img.naturalHeight) return;
      var box = img.getBoundingClientRect();
      if (!box.width || !box.height) return;
      var scale = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
      var inset = 12;
      tag.style.left = Math.round((box.width - img.naturalWidth * scale) / 2 + inset) + 'px';
      tag.style.top = Math.round((box.height - img.naturalHeight * scale) / 2 + inset) + 'px';
    }

    place();
    var img = gallery.querySelector('img');
    if (img && !img.complete) img.addEventListener('load', place);
    window.addEventListener('resize', place);
    /* The gallery swaps the image in place when a thumbnail is chosen, and a
       different photograph can be a different shape. */
    new MutationObserver(place).observe(gallery, {
      attributes: true, subtree: true, attributeFilter: ['src'],
    });
  }

  /** Money in the shape the server already used, so the two never disagree. */
  function shapeMoney(sample, value) {
    var run = String(sample || '').match(/[\d.,]+/);
    if (!run) return value.toFixed(2);
    var prefix = String(sample).slice(0, run.index);
    var suffix = String(sample).slice(run.index + run[0].length);
    return prefix + value.toFixed(2) + suffix;
  }

  /*
   * The pair, priced.
   *
   * Two prices, what they come to together, and what that saves. The saving is
   * the persuading part and it is the one thing a badge cannot say: "10% off"
   * is a rule, "you save £41.50" is a reason.
   *
   * Only drawn when the arithmetic can actually be done. A missing price is
   * not worth a guess, so the panel falls back to naming the suit.
   */
  function bundle(panel, ownPrice) {
    var them = panel.product;
    if (!them || them.amount == null || !ownPrice || !panel.percentage) return null;

    var full = ownPrice + them.amount;
    var after = full * (1 - panel.percentage / 100);
    var saved = full - after;
    var sample = them.price || '';

    var box = el('div', 'tc-panel__sums');
    [
      ['Both suits', shapeMoney(sample, full), 'was'],
      ['Together', shapeMoney(sample, after), 'now'],
      ['You save', shapeMoney(sample, saved), 'save'],
    ].forEach(function (row) {
      /* Order matters as much as size: what it was, what it is, what that
         leaves you. The saving is last because it is the conclusion. */
      var cell = el('div', 'tc-panel__sum tc-panel__sum--' + row[2]);
      cell.appendChild(el('span', 'tc-panel__sum-key', row[0]));
      cell.appendChild(el('span', 'tc-panel__sum-val', row[1]));
      box.appendChild(cell);
    });
    return box;
  }

  /*
   * What this product costs, taken from the page rather than fetched.
   *
   * Liquid has already printed it in the customer's own market and currency,
   * so reading it back is both cheaper than another request and guaranteed to
   * agree with the number beside it. Null when it cannot be read, which turns
   * the arithmetic off rather than guessing at it.
   */
  function ownPrice() {
    var node = document.querySelector('[data-tc-panels]');
    var raw = node && node.getAttribute('data-price');
    var value = raw == null ? NaN : Number(raw) / 100;
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  /** One suit in the pair: picture, whose it is, name, price. */
  function suitCard(who, title, image, price, url, extra) {
    var card = el(url ? 'a' : 'div', 'tc-pair__suit');
    if (url) card.href = url;

    if (image) {
      var img = el('img', 'tc-pair__img');
      img.src = image;
      img.alt = title || '';
      img.loading = 'lazy';
      card.appendChild(img);
    }

    var body = el('span', 'tc-pair__body');
    body.appendChild(el('span', 'tc-pair__who', who));
    body.appendChild(el('span', 'tc-pair__name', title || ''));
    if (price) body.appendChild(el('span', 'tc-pair__price', price));
    if (extra) body.appendChild(el('span', 'tc-pair__extra', extra));
    card.appendChild(body);
    return card;
  }

  /*
   * Both suits, in one action.
   *
   * The father is already choosing his own size when he meets the offer, so
   * the boy's should not cost him a page. A modal over the suit he is on is
   * the shortest honest route: pick an age, press once, and the basket has
   * both.
   *
   * Only offered from the man's side. His suit is sold as three pieces with a
   * size each, and asking somebody on the boy's page to choose a blazer, a
   * waistcoat and a pair of trousers for a man who is not there is a worse
   * experience than a link. From that side the card stays a link.
   */

  /** The men's suit as it is currently configured on the page. */
  function mensItems() {
    var rows = document.querySelectorAll('[data-tcsep-row]');
    if (rows.length) {
      var items = [];
      var incomplete = false;
      Array.prototype.forEach.call(rows, function (row) {
        var tick = row.querySelector('[data-tcsep-tick]');
        if (tick && !tick.checked) return;              // deselected piece
        var size = row.querySelector('[data-tcsep-size][aria-pressed="true"]');
        if (!size) { incomplete = true; return; }
        items.push({ id: Number(size.getAttribute('data-id')), quantity: 1 });
      });
      return incomplete || !items.length ? null : items;
    }

    /* A suit that is not sold in pieces: the form's own variant. */
    var form = document.querySelector('form[action*="/cart/add"]');
    var id = form && form.querySelector('[name="id"]');
    return id && id.value ? [{ id: Number(id.value), quantity: 1 }] : null;
  }

  function pairModal(panel) {
    var them = panel.product;
    var dialog = el('dialog', 'tc-pairbox');

    var head = el('div', 'tc-pairbox__head');
    head.appendChild(el('h2', 'tc-pairbox__title', panel.heading));
    var close = el('button', 'tc-pairbox__close', '\u00d7');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { dialog.close(); });
    head.appendChild(close);
    dialog.appendChild(head);

    /*
     * Both suits and the arithmetic, which used to be on the page.
     *
     * They belong here now that the page carries a list: the saving is the
     * argument for pressing the button, and the button is in this sheet.
     */
    var node = document.querySelector('[data-tc-panels]');
    var mineTitle = node ? node.getAttribute('data-title') : '';
    var mineImage = node ? node.getAttribute('data-image') : '';
    var minePrice = ownPrice();

    var pair = el('div', 'tc-pair');
    pair.appendChild(suitCard('This suit', mineTitle, mineImage,
      minePrice ? shapeMoney(them.price, minePrice) : null, null, null));
    pair.appendChild(el('span', 'tc-pair__plus', '+'));
    pair.appendChild(suitCard('His', them.title,
      them.image ? them.image + (them.image.indexOf('?') > -1 ? '&' : '?') + 'width=200' : null,
      them.price, null, null));
    dialog.appendChild(pair);

    var sums = bundle(panel, minePrice);
    if (sums) dialog.appendChild(sums);

    dialog.appendChild(el('p', 'tc-pairbox__label', 'Choose his size'));

    var chosen = null;
    var grid = el('div', 'tc-pairbox__sizes');
    (them.variants || []).forEach(function (v) {
      var b = el('button', 'tc-pairbox__size', v.title);
      b.type = 'button';
      if (!v.available) {
        b.disabled = true;
        b.title = 'Sold out';
      }
      b.addEventListener('click', function () {
        chosen = v;
        Array.prototype.forEach.call(grid.children, function (o) {
          o.setAttribute('aria-pressed', String(o === b));
        });
        say('');
        /* Picking a size after it is in the bag is how a size is corrected,
           so the button has to become the way to do that rather than staying
           on "Added" and leaving no way back. */
        if (added) restate();
      });
      b.setAttribute('aria-pressed', 'false');
      grid.appendChild(b);
    });
    dialog.appendChild(grid);

    var note = el('p', 'tc-pairbox__note', '');
    note.hidden = true;
    dialog.appendChild(note);
    function say(text, bad) {
      note.textContent = text;
      note.hidden = !text;
      note.classList.toggle('is-bad', !!bad);
    }

    var add = el('button', 'tc-pairbox__add', 'Add both to bag');
    add.type = 'button';

    /*
     * What is in the bag, and what the button should therefore say.
     *
     * The sheet used to close itself the moment the add succeeded, so the last
     * thing the customer saw was a button reading "Adding…" and a sheet
     * vanishing — no confirmation, and no way to correct a size they had just
     * realised was wrong without finding the panel again. It stays open now and
     * says "Added"; picking another size turns the same button into the way to
     * change it.
     *
     * `added` is his variant id, so a size change swaps that line and leaves the
     * suit alone — re-adding both would put a second suit in the bag.
     */
    var added = null;

    function restate() {
      var changing = added && chosen && Number(chosen.id.replace(/\D/g, '')) !== added;
      add.disabled = false;
      add.classList.toggle('is-added', !!added && !changing);
      if (changing) {
        add.textContent = 'Change his size to ' + chosen.title;
        say('');
      } else if (added) {
        add.textContent = 'Added to bag';
        add.disabled = true;
        say('Both are in your bag. Pick another size above if his needs changing.');
      } else {
        add.textContent = 'Add both to bag';
      }
    }

    add.addEventListener('click', function () {
      if (!chosen) return say('Choose his size first.', true);
      var his = Number(chosen.id.replace(/\D/g, ''));

      var mine = mensItems();
      /* Only on the first add. A size change must not need the suit's own
         picker to still be answered, and must not add the suit twice. */
      if (!added && !mine) {
        return say('Choose your own size on the page behind first — then both go in together.', true);
      }

      add.disabled = true;
      add.textContent = added ? 'Changing\u2026' : 'Adding\u2026';

      /* A change is one request that zeroes the old line and sets the new one,
         so the bag is never briefly missing his suit. */
      var request = added
        ? (function () {
            var updates = {};
            updates[added] = 0;
            updates[his] = 1;
            return fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates }),
            });
          })()
        : fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: mine.concat([{ id: his, quantity: 1 }]) }),
          });

      request
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && res.body.description) || 'That could not be added.');
          added = his;
          /* Told the way the theme tells it: the drawer listens to both, the
             header's basket count only to the first. The drawer is deliberately
             not opened — it would cover the sheet the customer is still using. */
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          restate();
        })
        .catch(function (err) {
          add.disabled = false;
          restate();
          say(err.message || 'That could not be added.', true);
        });
    });
    dialog.appendChild(add);

    dialog.appendChild(el('p', 'tc-pairbox__small',
      panel.note ? panel.note + ' The discount applies while both suits are in your bag.'
                 : 'The discount applies while both suits are in your bag.'));

    /* Clicking the backdrop closes it; clicking the card must not. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    return dialog;
  }

  /*
   * The free shirt, in two steps.
   *
   * Their version of this is two links into a collection: leave the suit,
   * find a shirt, add it, and trust something happens at the till. Ours
   * happens over the suit — but not all at once. Nineteen shirts and forty
   * colour-and-size chips on one sheet is a wall, and the customer has only
   * one question at a time: which shirt, then which one of it.
   *
   * So the sheet shows the shirts, and turns into the shirt once one is
   * chosen. Back is always there, because choosing the wrong shirt should
   * cost a click and not the sheet.
   */
  function giftSheet(panel) {
    var offer = panel.gift;
    var dialog = el('dialog', 'tc-pairbox tc-giftbox');
    var picked = null;
    var pickedColour = null;
    var pickedVariant = null;

    var head = el('div', 'tc-pairbox__head');
    var back = el('button', 'tc-giftbox__back', '\u2190');
    back.type = 'button';
    back.setAttribute('aria-label', 'Back to the shirts');
    back.hidden = true;
    back.addEventListener('click', function () { picked = null; draw(); });
    head.appendChild(back);

    var title = el('h2', 'tc-pairbox__title', panel.heading);
    head.appendChild(title);

    var close = el('button', 'tc-pairbox__close', '\u00d7');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { dialog.close(); });
    head.appendChild(close);
    dialog.appendChild(head);

    var stage = el('div', 'tc-giftbox__stage');
    dialog.appendChild(stage);

    var note = el('p', 'tc-pairbox__note', '');
    note.hidden = true;
    dialog.appendChild(note);
    function say(text, bad) {
      note.textContent = text;
      note.hidden = !text;
      note.classList.toggle('is-bad', !!bad);
    }

    var add = el('button', 'tc-pairbox__add', 'Add suit and free shirt');
    add.type = 'button';
    add.hidden = true;
    dialog.appendChild(add);

    var small = el('p', 'tc-pairbox__small',
      'The shirt comes off at checkout while the suit is in your bag.');
    dialog.appendChild(small);

    /*
     * Colour and size arrive welded together in the variant title — "Blue /
     * Small", "White / Small / Regular Fit". Splitting on the separator gives
     * the colour back, and asking for it first turns forty chips into five
     * and then eight.
     */
    function segments(v) { return String(v.title).split(' / '); }
    function hasColours(variants) {
      if (!variants.length || segments(variants[0]).length < 2) return false;
      var seen = {};
      variants.forEach(function (v) { seen[segments(v)[0]] = true; });
      return Object.keys(seen).length > 1;
    }
    function coloursOf(variants) {
      var out = [];
      variants.forEach(function (v) {
        var c = segments(v)[0];
        if (out.indexOf(c) === -1) out.push(c);
      });
      return out;
    }
    function rest(v) { return segments(v).slice(1).join(' / ') || v.title; }

    function chip(text, on, onPick) {
      var b = el('button', 'tc-pairbox__size', text);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(!!on));
      b.addEventListener('click', onPick);
      return b;
    }

    /*
     * "A shirt or a tie".
     *
     * A promotion may name more than one collection, and then the first
     * question is not which shirt but which kind of thing. Only asked when
     * there is more than one: a single-collection offer goes straight to the
     * garments, because a chooser with one option is a question with one
     * answer.
     */
    var groups = (offer.groups || []).filter(Boolean);
    var pickedGroup = groups.length > 1 ? null : (groups[0] || null);

    /*
     * One preview, moved about, rather than one per tile: nineteen hidden
     * images would be nineteen more requests for a picture most people never
     * ask to see.
     */
    var preview = null;

  /** Whether this device has a pointer that can hover and, crucially, leave. */
  var CAN_HOVER = !!(window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches);

    function showPreview(tile, shirt) {
      if (!preview) {
        preview = el('div', 'tc-giftbox__preview');
        var pimg = el('img', 'tc-giftbox__previewimg');
        preview.appendChild(pimg);
        preview.appendChild(el('span', 'tc-giftbox__previewname', ''));
        /*
         * Inside the dialog, not on the body.
         *
         * A modal <dialog> is painted in the browser's top layer, and nothing
         * outside it can be brought in front — no z-index reaches. Appended to
         * the body the preview was drawn underneath the sheet, which looks
         * like it opened below the shirts rather than beside them.
         */
        dialog.appendChild(preview);
      }
      preview.querySelector('.tc-giftbox__previewimg').src =
        shirt.image + (shirt.image.indexOf('?') > -1 ? '&' : '?') + 'width=520';
      preview.querySelector('.tc-giftbox__previewname').textContent = shirt.title || '';
      /* Made visible first so offsetHeight is a real number: a hidden element
         measures zero, and the centring below would then be against nothing. */
      preview.hidden = false;

      /* Beside the tile, and inside the window: on a narrow screen there is no
         room to the right, so it goes to the left instead of off the edge. */
      /*
       * Beside the shirt it belongs to: level with the tile, to its right, and
       * flipped to the left when the sheet is close enough to the edge that
       * there is no room. Clamped top and bottom so a tile near the foot of
       * the grid does not push it off the screen.
       */
      var box = tile.getBoundingClientRect();
      var w = 240;
      var h = preview.offsetHeight || 320;

      var left = box.right + 12;
      if (left + w > window.innerWidth - 8) left = box.left - w - 12;
      if (left < 8) left = Math.min(box.right + 12, window.innerWidth - w - 8);

      var top = box.top + box.height / 2 - h / 2;
      top = Math.max(8, Math.min(top, window.innerHeight - h - 8));

      preview.style.left = Math.round(left) + 'px';
      preview.style.top = Math.round(top) + 'px';
      preview.hidden = false;
    }

    function hidePreview() {
      if (preview) preview.hidden = true;
    }

    /* It must not outlive the sheet, or it hangs over the page. */
    dialog.addEventListener('close', function () {
      if (preview) { preview.remove(); preview = null; }
    });

    function drawShirts() {
      back.hidden = true;
      add.hidden = true;
      title.textContent = panel.heading;
      stage.textContent = '';

      if (groups.length > 1) {
        stage.appendChild(el('p', 'tc-pairbox__label', 'Choose what you would like'));
        var kinds = el('div', 'tc-pairbox__sizes');
        groups.forEach(function (g) {
          kinds.appendChild(chip(g, pickedGroup === g, function () {
            pickedGroup = g;
            drawShirts();
          }));
        });
        stage.appendChild(kinds);
        if (!pickedGroup) return;
      }

      /* The collection's own name only earns a mention when there was a
         choice of them: with one it reads "choose your men's formal shirts",
         which is a shelf label rather than an instruction. */
      stage.appendChild(el('p', 'tc-pairbox__label',
        groups.length > 1 && pickedGroup
          ? 'Choose your ' + pickedGroup.toLowerCase()
          : 'Choose your shirt'));

      var grid = el('div', 'tc-giftbox__grid');
      (offer.choices || [])
        .filter(function (c) { return !pickedGroup || c.group === pickedGroup; })
        .forEach(function (shirt) {
        var tile = el('button', 'tc-giftbox__tile');
        tile.type = 'button';
        if (shirt.image) {
          var img = el('img', 'tc-giftbox__img');
          img.src = shirt.image + (shirt.image.indexOf('?') > -1 ? '&' : '?') + 'width=220';
          img.alt = shirt.title || '';
          img.loading = 'lazy';
          tile.appendChild(img);
        }
        tile.appendChild(el('span', 'tc-giftbox__name', shirt.title || ''));
        /* Struck: it is worth something, and a shirt with no price on it
           reads as the cheap one being palmed off. */
        if (shirt.price) tile.appendChild(el('span', 'tc-giftbox__was', shirt.price));
        tile.addEventListener('click', function () {
          hidePreview();
          picked = shirt;
          pickedColour = null;
          pickedVariant = null;
          say('');
          draw();
        });

        /*
         * A bigger picture on hover.
         *
         * The tiles are small so that nineteen shirts fit without scrolling
         * to find them, which makes a white shirt hard to tell from another
         * white shirt — a collar and a cuff are the whole difference. The
         * preview is the same photograph at a size where that shows, and it
         * costs no room on the sheet because it is only there while the
         * pointer is.
         */
        /*
         * Only where there is a pointer to leave with.
         *
         * A phone fires mouseenter on a tap and frequently never fires
         * mouseleave, so the preview opened over the grid and stayed there —
         * covering the very shirts it was meant to help choose between, with
         * nothing to dismiss it. The same tap had already picked the shirt, so
         * it was in the way of a decision that had been made.
         *
         * Touch does not need it anyway: picking a shirt draws it full size on
         * the next step, which is the bigger picture the hover was standing in
         * for. `(hover: hover)` is the question actually being asked — not the
         * width of the screen, which says nothing about how it is pointed at.
         */
        if (shirt.image && CAN_HOVER) {
          tile.addEventListener('mouseenter', function () { showPreview(tile, shirt); });
          tile.addEventListener('focus', function () { showPreview(tile, shirt); });
          tile.addEventListener('mouseleave', hidePreview);
          tile.addEventListener('blur', hidePreview);
        }
        grid.appendChild(tile);
      });
      stage.appendChild(grid);
    }

    function drawOne() {
      back.hidden = false;
      add.hidden = false;
      title.textContent = 'Your free shirt';
      stage.textContent = '';

      var card = el('div', 'tc-pairbox__suit');
      if (picked.image) {
        var img = el('img', 'tc-pairbox__img');
        img.src = picked.image + (picked.image.indexOf('?') > -1 ? '&' : '?') + 'width=300';
        img.alt = picked.title || '';
        card.appendChild(img);
      }
      var body = el('div', 'tc-pairbox__body');
      body.appendChild(el('span', 'tc-pairbox__name', picked.title || ''));
      var price = el('span', 'tc-giftbox__free', 'FREE');
      if (picked.price) {
        var was = el('span', 'tc-giftbox__was', picked.price);
        body.appendChild(was);
      }
      body.appendChild(price);
      card.appendChild(body);
      stage.appendChild(card);

      var variants = picked.variants || [];

      if (hasColours(variants)) {
        stage.appendChild(el('p', 'tc-pairbox__label', 'Colour'));
        var colours = el('div', 'tc-pairbox__sizes');
        coloursOf(variants).forEach(function (c) {
          colours.appendChild(chip(c, pickedColour === c, function () {
            pickedColour = c;
            pickedVariant = null;
            say('');
            drawOne();
          }));
        });
        stage.appendChild(colours);
        if (!pickedColour) return;
      }

      var forColour = hasColours(variants) && pickedColour
        ? variants.filter(function (v) { return segments(v)[0] === pickedColour; })
        : variants;

      stage.appendChild(el('p', 'tc-pairbox__label', 'Size'));
      var sizes = el('div', 'tc-pairbox__sizes');
      forColour.forEach(function (v) {
        sizes.appendChild(chip(
          hasColours(variants) ? rest(v) : v.title,
          pickedVariant && pickedVariant.id === v.id,
          function () {
            pickedVariant = v;
            say('');
            Array.prototype.forEach.call(sizes.children, function (o) {
              o.setAttribute('aria-pressed', String(o.textContent === (hasColours(variants) ? rest(v) : v.title)));
            });
          }
        ));
      });
      stage.appendChild(sizes);
    }

    function draw() { picked ? drawOne() : drawShirts(); }

    add.addEventListener('click', function () {
      if (!picked) return say('Choose a shirt first.', true);
      if (!pickedVariant) return say('Choose a size.', true);

      var mine = mensItems();
      if (!mine) {
        return say('Choose your suit size on the page behind first — then both go in together.', true);
      }

      add.disabled = true;
      add.textContent = 'Adding\u2026';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: mine.concat([{ id: Number(pickedVariant.id.replace(/\D/g, '')), quantity: 1 }]),
        }),
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && res.body.description) || 'That could not be added.');
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          dialog.close();
        })
        .catch(function (err) {
          add.disabled = false;
          add.textContent = 'Add suit and free shirt';
          say(err.message || 'That could not be added.', true);
        });
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    draw();
    return dialog;
  }

  /*
   * One panel, a tab per offer.
   *
   * Two offers meant two full panels stacked down the buying column, which was
   * a screen of promotion beside a screen of product. A list of one-line rows
   * fixed the height and lost the thing worth keeping: the pair shown as a
   * pair, with the arithmetic under it.
   *
   * Tabs keep both. One panel, one offer visible at a time, and each gets the
   * room to be itself. With a single offer there are no tabs at all — a tab
   * strip with one tab is a heading that looks broken.
   */

  /** What this offer is worth, said in the fewest words that are true. */
  function worthOf(panel) {
    if (panel.kind === 'pair' && panel.percentage) return 'Save ' + panel.percentage + '% on both';
    if (panel.kind === 'gift') {
      var most = (panel.gift && panel.gift.choices || []).reduce(function (a, c) {
        return c.amount && c.amount > a ? c.amount : a;
      }, 0);
      var sample = (panel.gift && panel.gift.choices || [])[0];
      return most ? 'Worth up to ' + shapeMoney(sample && sample.price, most) + ', free'
                  : 'Free with this suit';
    }
    if (panel.percentage) return 'Save ' + panel.percentage + '%';
    return panel.lead || '';
  }

  /** A short name for a tab: the calendar's title minus the number in it. */
  function shortName(panel) {
    return String(panel.heading || '').split(/\s[-\u2013\u2014]\s/)[0].trim();
  }

  /** The pair, as it was: both suits, the sums, and a way to add them. */
  /* The centred heading with a rule either side — the panels had one each
     before they were combined, and it is what made them look like something
     rather than a box of text. The tab above selects; this names. */
  function ruledHead(text) {
    var head = el('div', 'tc-panel__head');
    head.appendChild(el('span', 'tc-panel__title', text));
    return head;
  }

  function pairBody(panel) {
    var body = el('div', 'tc-panel__body');
    body.appendChild(ruledHead(shortName(panel)));
    body.appendChild(el('p', 'tc-panel__sub',
      'Buy the matching pair and save ' + panel.percentage + '% on both'));

    var node = document.querySelector('[data-tc-panels]');
    var them = panel.product;
    var mine = ownPrice();

    var pair = el('div', 'tc-pair');
    pair.appendChild(suitCard('This suit',
      node ? node.getAttribute('data-title') : '',
      node ? node.getAttribute('data-image') : '',
      mine ? shapeMoney(them && them.price, mine) : null, null, null));
    pair.appendChild(el('span', 'tc-pair__plus', '+'));

    var canAdd = panel.role !== 'boys'
      && them && (them.variants || []).some(function (v) { return v.available; });

    var card = suitCard(panel.role === 'boys' ? "Dad's" : 'His',
      them && them.title,
      them && them.image ? them.image + (them.image.indexOf('?') > -1 ? '&' : '?') + 'width=200' : null,
      them && them.price,
      canAdd ? null : (them && them.url),
      canAdd ? 'Choose his size →' : 'View →');

    if (canAdd) {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      var box = null;
      var open = function (e) {
        e.preventDefault();
        if (!box) { box = pairModal(panel); document.body.appendChild(box); }
        box.showModal();
      };
      card.addEventListener('click', open);
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') open(e);
      });
    }
    pair.appendChild(card);
    body.appendChild(pair);

    var sums = bundle(panel, mine);
    if (sums) body.appendChild(sums);
    if (panel.note) body.appendChild(el('p', 'tc-panel__note', panel.note));
    return body;
  }

  /** The gift: what it is worth, and the way in to choosing it. */
  function giftBody(panel) {
    var body = el('div', 'tc-panel__body');
    var offer = panel.gift;
    /*
     * Not said twice on one screen.
     *
     * The ribbon on the photograph already reads FREE SHIRT, and this heading
     * said it again a few centimetres below in white — which read as a second
     * badge rather than as the panel's title. Where the ribbon is carrying this
     * offer the panel goes straight to the part the ribbon cannot do: what the
     * shirt is worth, and choosing it.
     */
    if (RIBBONED !== panel.id) body.appendChild(ruledHead(shortName(panel)));

    if (!offer.eligible) {
      body.appendChild(el('p', 'tc-panel__locked', offer.because || 'Not available on this suit'));
      if (offer.minSpend) {
        body.appendChild(el('p', 'tc-panel__note',
          'Full-price suits over ' + offer.minSpend + ' come with one.'));
      }
      return body;
    }

    body.appendChild(el('p', 'tc-panel__sub', worthOf(panel)));

    var open = el('button', 'tc-panel__button', 'Choose your free shirt →');
    open.type = 'button';
    var sheet = null;
    open.addEventListener('click', function () {
      if (!sheet) { sheet = giftSheet(panel); document.body.appendChild(sheet); }
      sheet.showModal();
    });
    body.appendChild(open);

    if ((offer.choices || []).length) {
      body.appendChild(el('p', 'tc-panel__choices', offer.choices.length + ' to choose from'));
    }
    if (panel.note) body.appendChild(el('p', 'tc-panel__note', panel.note));
    return body;
  }

  function bodyFor(panel) {
    if (panel.kind === 'pair' && panel.product) return pairBody(panel);
    if (panel.gift) return giftBody(panel);
    var body = el('div', 'tc-panel__body');
    if (panel.lead) body.appendChild(el('p', 'tc-panel__sub', panel.lead));
    if (panel.note) body.appendChild(el('p', 'tc-panel__note', panel.note));
    return body;
  }

  function offersView(panels) {
    var root = el('div', 'tc-panel');

    /* No outer title. Each body carries its own ruled heading, as the
       standalone panels did, and a second title above the tabs would name
       the same offer twice. */
    if (panels.length === 1) {
      root.appendChild(bodyFor(panels[0]));
      return root;
    }

    var tabs = el('div', 'tc-tabs');
    tabs.setAttribute('role', 'tablist');
    var holder = el('div', 'tc-tabs__body');

    function show(i) {
      Array.prototype.forEach.call(tabs.children, function (b, n) {
        b.setAttribute('aria-selected', String(n === i));
      });
      holder.textContent = '';
      holder.appendChild(bodyFor(panels[i]));
    }

    panels.forEach(function (panel, i) {
      var tab = el('button', 'tc-tabs__tab');
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.appendChild(el('span', 'tc-tabs__name', shortName(panel)));
      tab.appendChild(el('span', 'tc-tabs__worth',
        panel.gift && !panel.gift.eligible ? 'Not on this suit' : worthOf(panel)));
      tab.addEventListener('click', function () { show(i); });
      tabs.appendChild(tab);
    });

    root.appendChild(tabs);
    root.appendChild(holder);
    root.classList.add('tc-panel--tabbed');
    /* The first offer this suit actually earns, so the panel does not open on
       a refusal when there is something to say instead. */
    var first = panels.findIndex(function (p) { return !(p.gift && !p.gift.eligible); });
    show(first > -1 ? first : 0);
    return root;
  }

  /*
   * Previewing an offer that has not opened yet.
   *
   * Add ?promo_preview=1 to any product URL and scheduled or draft promotions
   * are drawn as they will appear. Remembered for the tab, because Shopify
   * strips its own preview parameter on the first redirect and the next click
   * would otherwise drop back to showing nothing.
   *
   * ?promo_preview=0 forgets it again.
   */
  var preview = false;
  try {
    var asked = new URLSearchParams(location.search).get('promo_preview');
    if (asked === '1') sessionStorage.setItem('tc_promo_preview', '1');
    if (asked === '0') sessionStorage.removeItem('tc_promo_preview');
    preview = sessionStorage.getItem('tc_promo_preview') === '1';
  } catch (e) {
    /* A browser refusing storage still previews for this one page. */
    preview = new URLSearchParams(location.search).get('promo_preview') === '1';
  }

  var url = '/apps/tru/panels?handle=' + encodeURIComponent(handle) + (preview ? '&preview=1' : '');

  fetch(url, { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var panels = ((data && data.panels) || []).filter(function (p) { return p && p.heading; });
      if (!panels.length) return;

      /* One ribbon at most, and only for an offer this product has earned —
         two corners on one photograph is a sale rack, not a suit. Chosen before
         the panels are built, because a panel needs to know whether the ribbon
         is already saying its name. */
      var first = panels.filter(function (p) {
        return p.badge && !(p.gift && !p.gift.eligible);
      })[0];
      if (first) RIBBONED = first.id;

      mount.appendChild(offersView(panels));
      if (first) ribbon(first);

      if (preview) {
        var flag = el('p', 'tc-panel__preview',
          'Preview — showing offers that have not started yet. Customers do not see this.');
        mount.appendChild(flag);
      }
      mount.hidden = false;
    })
    .catch(function (err) {
      /*
       * A promotion nobody can fetch is a promotion nobody is shown — the page
       * is complete without it, so this never interrupts anybody.
       *
       * It does say so in the console, though. This catch was silent, and it
       * sat across the rendering as well as the request: a mistake in drawing
       * a panel produced a page with no panel, no error and nothing to go on.
       */
      if (window.console) console.warn('[promo-panels]', err && err.message ? err.message : err);
    });
})();

/* build: offers list */
